import { Star } from 'lucide-react';

import { cn } from '@/lib/utils/cn';
import { MODULE_ACL, USER_ROLES } from '@/utilities/constant';
import { formatDateTime } from '@/utilities/helpers';
import { getUser } from '@/utilities/setting';
import { parseModuleOperations } from '@/utilities/voicePlanAcl';

export interface FeedbackOption {
  key: string;
  label: string;
}

export interface FeedbackSection {
  key: string;
  title: string;
  required?: boolean;
  options: FeedbackOption[];
}

export interface CallQualityForm {
  name: string;
  rating_question: string;
  rating_required: boolean;
  threshold: number;
  sections: FeedbackSection[];
  comment_enabled: boolean;
  comment_required: boolean;
  updated_at?: string;
}

export interface CallQualityFeedback {
  id: number;
  trace_id: string | null;
  call_sid: string | null;
  agent_id: number | null;
  agent_name: string | null;
  agent_number: string | null;
  customer_number?: string | null;
  rating: number | null;
  threshold: number | null;
  issues: FeedbackSection[];
  comment: string | null;
  form_name: string | null;
  rating_question: string | null;
  submitted_at: string;
}

export type CallQualityOp = 'view_permission' | 'read' | 'add';

export function canCallQualityFeedback(op: CallQualityOp): boolean {
  const user = getUser();
  if (user?.role === USER_ROLES.SUPERADMIN) return true;
  const ops = parseModuleOperations(user?.acl_settings?.module_operations);
  return !!ops?.[MODULE_ACL.CALL_QUALITY_FEEDBACK]?.[op];
}

/** Report rows carry `call_quality_feedback` only when the backend allowed `read`. */
export const feedbackOf = (row: any): CallQualityFeedback[] =>
  Array.isArray(row?.call_quality_feedback) ? row.call_quality_feedback : [];

const issueCount = (f: CallQualityFeedback) =>
  (f.issues || []).reduce((n, s) => n + (s.options?.length || 0), 0);

export const issuesText = (issues: FeedbackSection[] = []) =>
  issues.map((s) => `${s.title}: ${(s.options || []).map((o) => o.label).join(', ')}`).join(' | ');

/** Excel/CSV columns for one call (several agents may each have rated it). */
export function feedbackExportColumns(list: CallQualityFeedback[]) {
  const many = list.length > 1;
  const each = (fn: (f: CallQualityFeedback) => string) =>
    list.length ? list.map((f) => (many ? `${f.agent_name || 'Agent'}: ${fn(f) || '-'}` : fn(f) || '-')).join(' || ') : '-';
  return {
    'Call Audio Rating': each((f) => (f.rating == null ? '' : `${f.rating}/5`)),
    'Call Audio Issues': each((f) => issuesText(f.issues)),
    'Call Audio Comment': each((f) => f.comment || ''),
  };
}

export function StarRow({ value, size = 14, className }: { value: number | null; size?: number; className?: string }) {
  return (
    <span className={cn('inline-flex items-center gap-0.5', className)} aria-label={value == null ? 'No rating' : `${value} of 5 stars`}>
      {[1, 2, 3, 4, 5].map((n) => (
        <Star
          key={n}
          style={{ width: size, height: size }}
          className={value != null && n <= value ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground/40'}
        />
      ))}
    </span>
  );
}

/** Compact table cell: "★ 2/5 · 3 issues", or "-" when nothing was submitted. */
export function FeedbackCell({ row }: { row: any }) {
  const list = feedbackOf(row);
  const f = list[0];
  if (!f) return <span className="text-muted-foreground">-</span>;
  const issues = issueCount(f);
  return (
    <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs" title={issuesText(f.issues) || f.comment || ''}>
      <Star className="h-3.5 w-3.5 fill-amber-400 text-amber-400" />
      <span className="font-medium">{f.rating == null ? '-' : `${f.rating}/5`}</span>
      {issues > 0 && <span className="text-muted-foreground">· {issues} issue{issues > 1 ? 's' : ''}</span>}
      {list.length > 1 && <span className="text-muted-foreground">· +{list.length - 1}</span>}
    </span>
  );
}

/** Full read-only detail, used in the call-detail dialog, DID side panel and submissions view. */
export function FeedbackDetail({ list, showCustomer = false }: { list: CallQualityFeedback[]; showCustomer?: boolean }) {
  if (!list.length) return <p className="text-sm text-muted-foreground">No call audio feedback for this call.</p>;
  return (
    <div className="space-y-3">
      {list.map((f) => (
        <div key={f.id} className="space-y-2 rounded-md border p-3 text-sm">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <StarRow value={f.rating} />
              <span className="font-medium">{f.rating == null ? 'No rating' : `${f.rating}/5`}</span>
            </div>
            <span className="text-xs text-muted-foreground">{formatDateTime(f.submitted_at)}</span>
          </div>
          <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1 text-xs">
            <dt className="text-muted-foreground">Call ID</dt>
            <dd className="break-all font-mono">{f.call_sid || '-'}</dd>
            <dt className="text-muted-foreground">Trace ID</dt>
            <dd className="break-all font-mono">{f.trace_id || '-'}</dd>
            <dt className="text-muted-foreground">Agent</dt>
            <dd>{[f.agent_name, f.agent_number].filter(Boolean).join(' · ') || '-'}</dd>
            {showCustomer && (
              <>
                <dt className="text-muted-foreground">Customer</dt>
                <dd>{f.customer_number || '-'}</dd>
              </>
            )}
            <dt className="text-muted-foreground">Question</dt>
            <dd>{f.rating_question || '-'}</dd>
            <dt className="text-muted-foreground">Issues</dt>
            <dd>
              {f.issues?.length ? (
                <ul className="space-y-0.5">
                  {f.issues.map((s) => (
                    <li key={s.key}>
                      <span className="font-medium">{s.title}:</span> {s.options.map((o) => o.label).join(', ')}
                    </li>
                  ))}
                </ul>
              ) : (
                '-'
              )}
            </dd>
            <dt className="text-muted-foreground">Comment</dt>
            <dd className="whitespace-pre-wrap break-words">{f.comment || '-'}</dd>
          </dl>
        </div>
      ))}
    </div>
  );
}
